import { Link, NavLink } from 'react-router-dom';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../lib/session.js';
import { useResource } from '../lib/useResource.js';
import { useLocale, useT, useTPlural, LOCALES, type StringKey } from '../i18n/index.js';
import { Icon, ICONS } from './Icon.js';

/**
 * Platform console shell.
 *
 * Three fixed zones in the rail — brand, destinations, account — because an
 * operator should never have to scroll to find out who they are signed in as or
 * how to leave. Only the middle zone scrolls.
 */

interface NavEntry {
  to: string;
  key: StringKey;
  icon: string;
  end?: boolean;
}
interface NavSection {
  key: StringKey;
  items: NavEntry[];
}

/**
 * Grouped by what the work IS, not by page count: running the platform, taking
 * money for it, and being answerable for both. Five destinations do not need
 * sections to be findable — they need them to say what the console is for.
 */
const NAV: NavSection[] = [
  {
    key: 'nav.section.operations',
    items: [
      { to: '/', key: 'nav.overview', icon: ICONS.overview, end: true },
      { to: '/tenants', key: 'nav.tenants', icon: ICONS.operators },
      { to: '/routes', key: 'nav.routes', icon: ICONS.route },
    ],
  },
  {
    key: 'nav.section.money',
    items: [
      { to: '/invoices', key: 'nav.invoices', icon: ICONS.invoices },
      { to: '/promotions', key: 'nav.promotions', icon: ICONS.promotions },
    ],
  },
  {
    key: 'nav.section.governance',
    items: [
      { to: '/audit', key: 'nav.audit', icon: ICONS.audit },
      { to: '/staff', key: 'nav.staff', icon: ICONS.staff },
      { to: '/demo', key: 'nav.demo', icon: ICONS.demo },
    ],
  },
];

/** Closes a popover on outside click and on Escape, and restores focus. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

function initialsOf(name: string | undefined): string {
  return (name ?? 'BB')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]!)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/* ------------------------------------------------------------------ rail head */

function RailHeader() {
  const t = useT();
  return (
    <div className="board-grain flex h-[var(--bar-h)] shrink-0 items-center gap-3 border-b px-4"
         style={{ borderColor: 'var(--hairline)' }}>
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] font-display text-[0.9rem] font-extrabold"
        style={{ background: 'var(--signal)', color: '#14100A' }}
        aria-hidden
      >
        B
      </span>
      <span className="leading-none">
        <span className="block font-display text-[1.0625rem] font-extrabold tracking-tight">BusBook</span>
        <span className="mt-1 block text-[0.5625rem] font-semibold uppercase tracking-[0.22em]"
              style={{ color: 'var(--signal)' }}>
          {t('brand.suffix')}
        </span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ rail body */

function RailNav({ onNavigate }: { onNavigate?: () => void }) {
  const t = useT();
  return (
    <nav aria-label="Main" className="scroll-rail min-h-0 flex-1 overflow-y-auto px-2 pb-4">
      {NAV.map((section) => (
        <div key={section.key}>
          <p className="px-3 pb-1.5 pt-5 text-[0.625rem] font-semibold uppercase tracking-[0.18em]"
             style={{ color: 'var(--chalk-faint)' }}>
            {t(section.key)}
          </p>
          <ul className="flex flex-col gap-0.5">
            {section.items.map((entry) => (
              <li key={entry.to}>
                <NavLink
                  to={entry.to}
                  end={entry.end}
                  onClick={onNavigate}
                  className="relative flex items-center gap-3 rounded-lg py-2 pl-4 pr-3 text-[0.8125rem] font-medium transition-colors"
                  style={({ isActive }) => ({
                    color: isActive ? 'var(--chalk)' : 'var(--chalk-dim)',
                    background: isActive ? 'var(--ink-750)' : 'transparent',
                  })}
                >
                  {({ isActive }) => (
                    <span data-active={isActive} className="contents">
                      <span
                        className="signal-bar absolute inset-0 rounded-lg"
                        data-active={isActive ? 'true' : 'false'}
                        aria-hidden
                      />
                      <Icon d={entry.icon} className={isActive ? 'h-[1.125rem] w-[1.125rem]' : 'h-[1.125rem] w-[1.125rem] opacity-70'} />
                      <span>{t(entry.key)}</span>
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/* ---------------------------------------------------------------- rail footer */

/**
 * The account block. The brand mark IS the button — signing out lives behind the
 * person you are signed in as, which is where people look for it, rather than as
 * a bare link that competes with navigation for attention.
 */
function AccountMenu() {
  const t = useT();
  const { user, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const menuId = useId();

  return (
    <div ref={ref} className="relative shrink-0 border-t p-2" style={{ borderColor: 'var(--hairline)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-[var(--ink-750)]"
      >
        {/* The picture if there is one, initials otherwise. A stock silhouette would say
            "this person has no face"; initials say "no picture yet", which is what is true. */}
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            className="h-8 w-8 shrink-0 rounded-full object-cover"
            style={{ boxShadow: 'inset 0 0 0 1px var(--signal-line)' }}
          />
        ) : (
          <span
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[0.6875rem] font-bold"
            style={{ background: 'var(--ink-700)', color: 'var(--signal)', boxShadow: 'inset 0 0 0 1px var(--signal-line)' }}
            aria-hidden
          >
            {initialsOf(user?.name)}
          </span>
        )}
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block truncate text-[0.8125rem] font-semibold">{user?.name ?? '—'}</span>
          <span className="block truncate text-[0.6875rem]" style={{ color: 'var(--chalk-faint)' }}>
            {user?.email ?? ''}
          </span>
        </span>
        <Icon
          d={ICONS.chevron}
          className={`h-4 w-4 shrink-0 transition-transform ${open ? '-rotate-90' : 'rotate-90'}`}
        />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute bottom-[calc(100%+0.5rem)] left-2 right-2 overflow-hidden rounded-xl border"
          style={{ background: 'var(--ink-800)', borderColor: 'var(--hairline-strong)', boxShadow: 'var(--shadow-card)' }}
        >
          {/*
            Profile above sign-out, and nothing else. Language moved to the header, which
            left this menu doing the one job its label implies: your account.
          */}
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[0.8125rem] font-medium transition-colors hover:bg-[var(--ink-750)]"
            style={{ color: 'var(--chalk-dim)' }}
          >
            <Icon d={ICONS.user} className="h-4 w-4" />
            {t('shell.profile')}
          </Link>

          <div className="border-t" style={{ borderColor: 'var(--hairline)' }}>
            <button
              type="button"
              role="menuitem"
              onClick={signOut}
              className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[0.8125rem] font-medium transition-colors hover:bg-[var(--halt-dim)]"
              style={{ color: 'var(--halt)' }}
            >
              <Icon d={ICONS.signOut} className="h-4 w-4" />
              {t('shell.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- shell */

export function Shell({ children }: { children: ReactNode }) {
  const t = useT();
  const [drawer, setDrawer] = useState(false);

  const rail = (
    <>
      <RailHeader />
      <RailNav onNavigate={() => setDrawer(false)} />
      <AccountMenu />
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--ink-900)' }}>
      {/* Fixed rail on desktop. */}
      <aside
        className="fixed inset-y-0 left-0 z-30 hidden w-[var(--rail-w)] flex-col border-r lg:flex"
        style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
      >
        {rail}
      </aside>

      {/* Drawer on small screens. */}
      {drawer && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label={t('shell.closeMenu')}
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,.6)' }}
            onClick={() => setDrawer(false)}
          />
          <aside
            className="absolute inset-y-0 left-0 flex w-[var(--rail-w)] flex-col border-r"
            style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
          >
            {rail}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-[var(--rail-w)]">
        <MobileBar onOpen={() => setDrawer(true)} />
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}

function MobileBar({ onOpen }: { onOpen: () => void }) {
  const t = useT();
  return (
    <div
      className="flex h-14 items-center gap-3 border-b px-4 lg:hidden"
      style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
    >
      <button type="button" onClick={onOpen} aria-label={t('shell.openMenu')} className="p-1">
        <Icon d={ICONS.menu} className="h-5 w-5" />
      </button>
      <span className="font-display text-[0.9375rem] font-extrabold">BusBook</span>
    </div>
  );
}

/* ------------------------------------------------------------------ language */

/**
 * Language, beside the notification bell.
 *
 * It used to live inside the account menu at the foot of the side nav — three taps down,
 * behind a control labelled with somebody's own name, which is a strange place to look for
 * a setting that changes the entire interface. A globe in the header is where people expect
 * it and, unlike the account menu, it is reachable without knowing whose account this is.
 */
function LanguageMenu() {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const menuId = useId();
  const current = LOCALES.find((option) => option.code === locale);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('shell.language')}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        className="flex h-9 items-center gap-1.5 rounded-lg border px-2 transition-colors"
        style={{
          borderColor: open ? 'var(--signal-line)' : 'var(--hairline-strong)',
          background: open ? 'var(--signal-dim)' : 'transparent',
          color: 'var(--chalk-dim)',
        }}
      >
        <Icon d={ICONS.globe} className="h-[1.125rem] w-[1.125rem]" />
        {/* The current language in two letters. A globe alone says "language settings
            live here" but not which one is on, and that is the question being asked. */}
        <span className="font-mono text-[0.6875rem] font-semibold" style={{ color: 'var(--signal)' }}>
          {current?.short ?? ''}
        </span>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 top-[calc(100%+0.5rem)] z-40 w-44 overflow-hidden rounded-xl border"
          style={{ background: 'var(--ink-800)', borderColor: 'var(--hairline-strong)', boxShadow: 'var(--shadow-card)' }}
        >
          {LOCALES.map((option) => (
            <button
              key={option.code}
              type="button"
              role="menuitemradio"
              aria-checked={locale === option.code}
              onClick={() => {
                setLocale(option.code);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[0.8125rem] transition-colors hover:bg-[var(--ink-750)]"
              style={{ color: locale === option.code ? 'var(--chalk)' : 'var(--chalk-dim)' }}
            >
              <span className="w-7 font-mono text-[0.6875rem]" style={{ color: 'var(--signal)' }}>
                {option.short}
              </span>
              <span className="flex-1">{option.label}</span>
              {locale === option.code && <Icon d={ICONS.check} className="h-3.5 w-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- notifications */

interface Overview {
  tenants: Record<string, number>;
  invoicesAwaitingPayment: number;
  tenantsOnboarding: number;
}

/**
 * Derived from real platform state rather than a notifications table.
 *
 * Everything here is something an administrator can act on: an operator waiting
 * to go live, an invoice that was sent and not settled, a suspended operator
 * whose passengers are seeing a maintenance notice. A feed that also carried
 * "things that merely happened" would train people to stop opening it.
 */
function useAttention() {
  const { data } = useResource<Overview>('/platform/overview');
  const tp = useTPlural();
  const t = useT();

  const items: Array<{ id: string; tone: string; text: string; hint: string; to: string }> = [];
  if (!data) return { items, count: 0 };

  if (data.tenantsOnboarding > 0) {
    items.push({
      id: 'onboarding',
      tone: 'var(--hold)',
      text: tp('notif.onboarding', data.tenantsOnboarding),
      hint: t('notif.onboardingHint'),
      to: '/tenants',
    });
  }
  if (data.invoicesAwaitingPayment > 0) {
    items.push({
      id: 'unpaid',
      tone: 'var(--signal)',
      text: tp('notif.unpaid', data.invoicesAwaitingPayment),
      hint: t('notif.unpaidHint'),
      to: '/invoices',
    });
  }
  const suspended = data.tenants?.SUSPENDED ?? 0;
  if (suspended > 0) {
    items.push({
      id: 'suspended',
      tone: 'var(--halt)',
      text: tp('notif.suspended', suspended),
      hint: t('notif.suspendedHint'),
      to: '/tenants',
    });
  }
  return { items, count: items.length };
}

function Notifications() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const { items, count } = useAttention();
  const panelId = useId();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('notif.open')}
        aria-expanded={open}
        aria-controls={panelId}
        className="relative grid h-9 w-9 place-items-center rounded-lg border transition-colors"
        style={{
          borderColor: open ? 'var(--signal-line)' : 'var(--hairline-strong)',
          background: open ? 'var(--signal-dim)' : 'transparent',
          color: 'var(--chalk-dim)',
        }}
      >
        <Icon d={ICONS.bell} className="h-[1.125rem] w-[1.125rem]" />
        {count > 0 && (
          <span
            className="absolute -right-1 -top-1 grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full px-1 text-[0.625rem] font-bold tabular"
            style={{ background: 'var(--signal)', color: '#14100A' }}
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 top-[calc(100%+0.5rem)] z-40 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border"
          style={{ background: 'var(--ink-800)', borderColor: 'var(--hairline-strong)', boxShadow: 'var(--shadow-card)' }}
        >
          <p className="border-b px-4 py-3 text-[0.625rem] font-semibold uppercase tracking-[0.18em]"
             style={{ borderColor: 'var(--hairline)', color: 'var(--chalk-faint)' }}>
            {t('notif.title')}
          </p>
          {items.length === 0 ? (
            <div className="px-4 py-6">
              <p className="text-[0.8125rem]">{t('notif.empty')}</p>
              <p className="mt-1 text-[0.75rem]" style={{ color: 'var(--chalk-faint)' }}>
                {t('notif.emptyHint')}
              </p>
            </div>
          ) : (
            <ul>
              {items.map((item) => (
                <li key={item.id}>
                  {/* Rows read like a board line: a lit status square, then the fact. */}
                  <NavLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 border-b px-4 py-3 transition-colors last:border-b-0 hover:bg-[var(--ink-750)]"
                    style={{ borderColor: 'var(--hairline)' }}
                  >
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-[2px]" style={{ background: item.tone }} aria-hidden />
                    <span className="min-w-0">
                      <span className="block text-[0.8125rem] font-medium tabular">{item.text}</span>
                      <span className="mt-0.5 block text-[0.75rem]" style={{ color: 'var(--chalk-faint)' }}>
                        {item.hint}
                      </span>
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- page head */

export function Page({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <header
        className="sticky top-0 z-20 border-b px-5 py-4 backdrop-blur lg:h-[var(--bar-h)] lg:px-8 lg:py-0"
        style={{ background: 'color-mix(in oklab, var(--ink-850) 88%, transparent)', borderColor: 'var(--hairline)' }}
      >
        <div className="flex h-full flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display truncate text-[1.0625rem] font-bold tracking-tight">{title}</h1>
            {description && (
              <p className="mt-0.5 truncate text-[0.75rem]" style={{ color: 'var(--chalk-faint)' }}>
                {description}
              </p>
            )}
          </div>
          {/* Actions never wrap: a two-line button in a 4rem bar is what a header
              looks like when it was only ever tested with a short label. */}
          <div className="flex shrink-0 items-center gap-2 [&_button]:whitespace-nowrap [&_select]:whitespace-nowrap">
            {actions}
            <LanguageMenu />
            <Notifications />
          </div>
        </div>
      </header>
      <div className="px-5 py-6 lg:px-8">{children}</div>
    </>
  );
}
